import axios from 'axios'
import { User, GetProfileResponse } from '@/types'

// Storage keys (sessionStorage, not localStorage)
const WEB_SESSION_TOKEN_KEY = 'dupr_web_session_token'
const WEB_SESSION_EXPIRY_KEY = 'dupr_web_session_expiry'

interface WebSessionValidationResponse {
  accessToken: string
  expiresIn: number // 900 seconds (15 min)
}

interface ValidateApiResult {
  status: 'SUCCESS' | 'FAILURE'
  results: WebSessionValidationResponse[]
  errors?: { message: string }[]
}

/**
 * Validate a web session token and exchange it for an access token
 * This calls the backend endpoint that consumes the single-use token
 * and returns a scoped JWT for web session use
 */
export const validateWebSessionToken = async (token: string): Promise<WebSessionValidationResponse> => {
  const response = await axios.create().get<ValidateApiResult>(
    '/auth/v1.0/web-session/validate',
    { params: { token } }
  )

  if (response.data.status !== 'SUCCESS' || !response.data.results?.[0]) {
    throw new Error(response.data.errors?.[0]?.message || 'Invalid or expired token')
  }

  return response.data.results[0]
}

/**
 * Fetch user profile using an explicit token
 * This bypasses the normal auth flow and uses the provided token directly
 */
export const getProfileWithToken = async (token: string): Promise<User> => {
  const response = await axios.create().get<GetProfileResponse>(
    '/user/v1.0/profile',
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return response.data.result
}

/**
 * Store web session token in sessionStorage
 * Uses sessionStorage so the token survives page refresh but not tab/browser close
 */
export const setWebSessionToken = (token: string, expiresInSeconds: number): void => {
  const expiryTimestamp = Date.now() + expiresInSeconds * 1000
  sessionStorage.setItem(WEB_SESSION_TOKEN_KEY, token)
  sessionStorage.setItem(WEB_SESSION_EXPIRY_KEY, expiryTimestamp.toString())
}

/**
 * Get the stored web session token from sessionStorage
 * Returns null if no token exists
 */
export const getWebSessionToken = (): string | null => {
  return sessionStorage.getItem(WEB_SESSION_TOKEN_KEY)
}

/**
 * Clear the web session token from sessionStorage
 */
export const clearWebSessionToken = (): void => {
  sessionStorage.removeItem(WEB_SESSION_TOKEN_KEY)
  sessionStorage.removeItem(WEB_SESSION_EXPIRY_KEY)
}

/**
 * Check if a web session token exists and is not expired
 */
export const isWebSessionValid = (): boolean => {
  const token = sessionStorage.getItem(WEB_SESSION_TOKEN_KEY)
  const expiry = sessionStorage.getItem(WEB_SESSION_EXPIRY_KEY)

  if (!token || !expiry) return false
  return Date.now() < parseInt(expiry, 10)
}
